import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import * as LucideIcons from "lucide-react";
import { api } from "../lib/api";
import OrnamentalDivider from "./OrnamentalDivider";

function getIcon(name) {
  return LucideIcons[name] || LucideIcons.Sparkles;
}

export default function Amenities({ content = {} }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/amenities").then(({ data }) => setItems(data || [])).catch(() => {});
  }, []);

  if (!items.length) return null;

  return (
    <section
      id="amenidades"
      data-testid="amenities-section"
      className="relative py-28 overflow-hidden bg-gradient-to-b from-[var(--c-bg-deep)] via-[var(--c-bg-forest)] to-[var(--c-bg-deep)]"
    >
      <div className="orb w-[480px] h-[480px] bg-[var(--c-bio-green)]/30 top-1/2 -left-32 -translate-y-1/2" />
      <div className="orb w-[420px] h-[420px] bg-[var(--c-firefly-gold)]/30 top-32 -right-40" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="text-center max-w-3xl mx-auto"
        >
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] font-medium text-[var(--c-firefly-gold)]">
            <LucideIcons.Sparkles className="w-4 h-4" /> {content.amenities_overline || "Vivir el cuento"}
          </span>
          <h2 className="mt-5 font-serif text-4xl sm:text-5xl text-[var(--c-text)] leading-[1.1]">
            {content.amenities_title_1 || "12 espacios mágicos"}
            <span className="block font-fairy text-5xl text-[var(--c-bio-green)] mt-1">
              {content.amenities_title_fairy || "para perderte y reencontrarte"}
            </span>
          </h2>
          <OrnamentalDivider />
          <p className="text-[var(--c-text)]/75 text-lg">
            {content.amenities_description ||
              "Pextlán no son sólo lotes. Es un ecosistema diseñado para que cada día se sienta como salir de la rutina y entrar a un cuento."}
          </p>
        </motion.div>

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" style={{ perspective: "1200px" }}>
          {items.map((a, i) => {
            const Ico = getIcon(a.icon);
            return (
              <motion.div
                key={a.id}
                data-testid={`amenity-${i}`}
                initial={{ opacity: 0, y: 40, rotateX: 15 }}
                whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
                whileHover={{ rotateY: 8, rotateX: -6, y: -6, scale: 1.03 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.7, delay: (i % 3) * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="relative rounded-2xl p-6 bg-gradient-to-br from-[var(--c-bg-forest)]/80 to-[var(--c-bg-deep)]/80 border border-[var(--c-bio-green)]/15 backdrop-blur-md group cursor-default"
                style={{ transformStyle: "preserve-3d" }}
              >
                <div className="absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-r from-[var(--c-firefly-gold)]/40 via-[var(--c-bio-green)]/40 to-[var(--c-firefly-gold)]/40 blur-md pointer-events-none" />
                <div className="relative">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[var(--c-bio-green)]/30 to-[var(--c-firefly-gold)]/30 flex items-center justify-center border border-[var(--c-bio-green)]/40 shadow-[0_8px_30px_-10px_rgba(184,224,122,0.5)]">
                    <Ico className="w-6 h-6 text-[var(--c-bio-green)]" strokeWidth={1.7} />
                  </div>
                  <h3 className="mt-4 font-serif text-2xl text-[var(--c-text)] group-hover:text-[var(--c-firefly-gold)] transition-colors">
                    {a.title}
                  </h3>
                  <p className="mt-2 text-sm text-[var(--c-text)]/70 leading-relaxed">
                    {a.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
